import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@eti/providers';
import type { TripType } from '@eti/schema-types';
import { css } from '@eti/styles';
import { useFormContext } from 'react-hook-form';
import isFunction from 'lodash/isFunction';
import { breakpoints, Stack } from '@etg/wings';
import { useMediaQuery } from 'usehooks-ts';
import { AirplaneLanding, AirplaneTakeoff } from '@phosphor-icons/react';
import { tripTypes } from '../../../constants/tripTypesConstants';
import SwitchDirectionButton from './SwitchDirectionButton';
import LocationCombobox from './locationCombobox/LocationCombobox';
import ZeroWidthSpace from './ZeroWidthSpace';

const switchDirectionButtonStyles = css`
  height: 48px;
  width: 48px;
`;

const rtlMirrored = css`
  [dir='rtl'] & {
    transform: scaleX(-1);
  }
`;

const ariaLiveStyles = css`
  position: absolute;
`;

type OriginAndDestinationNewProps = {
  boundIndex?: number;
  destinationName: string;
  originName: string;
  selectedTripType?: TripType;
  setAreOriginAndDestinationFilled?: Dispatch<SetStateAction<boolean>>;
};

const OriginAndDestinationNew = ({
  boundIndex,
  destinationName,
  originName,
  selectedTripType,
  setAreOriginAndDestinationFilled,
}: OriginAndDestinationNewProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [switchLocationsLabel, setSwitchLocationsLabel] = useState<string | undefined>(undefined);
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._768})`);

  const onSwitchDirection = () => {
    const [origin, destination] = getValues([originName, destinationName]);

    setValue(destinationName, origin);
    setValue(originName, destination);
    setSwitchLocationsLabel(
      `${t('Searchform.Origin.Label')} ${destination.name}, ${t('Searchform.Destination.Label')} ${
        origin.name
      }`,
    );
  };

  const handleDestinationOnBlur = () => {
    const [origin, destination] = getValues([originName, destinationName]);

    if (destination !== null && origin !== null && isFunction(setAreOriginAndDestinationFilled)) {
      setAreOriginAndDestinationFilled(true);
    }
  };

  const getButtonDataTestId = () =>
    boundIndex && boundIndex >= 0
      ? `searchForm-multiBound[${boundIndex}]-exchangeLocations-button`
      : 'searchForm-singleBound-exchangeLocations-button';

  const getLocationDataTestId = (locationType: string) =>
    boundIndex && boundIndex >= 0
      ? `searchForm-multiBound[${boundIndex}]-${locationType}-input`
      : `searchForm-singleBound-${locationType}-input`;

  const originDataTestId = getLocationDataTestId('origin');
  const destinationDataTestId = getLocationDataTestId('destination');

  useEffect(() => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setSwitchLocationsLabel(undefined);
    }, 1000);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [switchLocationsLabel]);
  return (
    <>
      <LocationCombobox
        data-testid={originDataTestId}
        description={t('ScreenReader.SearchForm.From')}
        iconPrefix={<AirplaneTakeoff className={rtlMirrored} weight="light" />}
        id={originDataTestId}
        label={t('Searchform.Origin.Label')}
        name={originName}
        onSwitchDirection={onSwitchDirection}
        placeholder={t('Air.Search.CitiesSelection.Placeholder.From')}
      />
      {selectedTripType !== tripTypes.MULTI_STOP && isWiderViewport && (
        <Stack spacing={4}>
          <ZeroWidthSpace />
          <SwitchDirectionButton
            aria-label={t('Air.Search.SwitchLocations.Label')}
            className={switchDirectionButtonStyles}
            data-testid={getButtonDataTestId()}
            onClick={onSwitchDirection}
            title={t('Air.Search.SwitchLocations.Label')}
            type="button"
          />
        </Stack>
      )}
      <LocationCombobox
        data-testid={destinationDataTestId}
        description={t('ScreenReader.SearchForm.To')}
        iconPrefix={<AirplaneLanding className={rtlMirrored} weight="light" />}
        id={destinationDataTestId}
        label={t('Searchform.Destination.Label')}
        name={destinationName}
        onBlur={handleDestinationOnBlur}
        placeholder={t('Air.Search.CitiesSelection.Placeholder.To')}
      />
      <span
        aria-label={switchLocationsLabel}
        aria-live="assertive"
        className={ariaLiveStyles}
        role="alert"
      />
    </>
  );
};

export default OriginAndDestinationNew;
